import React, { useState, useRef } from "react";
import { ReactComponent as ArrowIcon } from "src/assets/icons/icon-arrow.svg";
import { useClickOutside } from "src/hooks";
import IconTooltip, { IconTooltipType } from "src/components/iconTooltip";
import "./dropdown-animation.scss";

export interface IField {
  key: string;
  value?: string;
  icon?: JSX.Element;
  status?: {
    key: string;
    description: string;
  };
}

interface IPropsDropdownAnimation {
  wrapperClass?: string;
  label?: string;
  customLabel?: React.ReactNode;
  selectedValue?: string | null;
  options: IField[];
  placeholder?: string;
  onChange: (newValue: IField) => void;
  disabled?: boolean;
  /** Hide the trailing chevron — e.g. when there's nothing to expand. */
  hideArrow?: boolean;
}

const DropdownAnimation: React.FC<IPropsDropdownAnimation> = ({
  wrapperClass = "",
  label = "",
  customLabel,
  selectedValue,
  options,
  placeholder,
  onChange,
  disabled = false,
  hideArrow = false,
}): JSX.Element => {
  const [isOpen, setOpen] = useState(false);
  const dropdownIdRef = useRef(`dropdown-${Math.random().toString(36).substr(2, 9)}`);
  const dropdownId = dropdownIdRef.current;

  const findSelectedOption = options?.find(({ key }: IField) => key === selectedValue);

  const { setListen } = useClickOutside({
    idElement: dropdownId,
    onClickOutside: (e: any) => {
      if (isOpen && !e.target.closest(".nova-text-input")) {
        onToggle();
      }
    },
  });

  const onToggle = () => {
    setOpen(!isOpen);
    setListen(!isOpen);
  };

  const onSelectOption = (option: IField) => {
    onChange(option);
    onToggle();
  };

  return (
    <div
      className={`dropdown-animation ${wrapperClass} ${options.some((option) => option.status) ? "dropdown-animation-with-status" : ""}`}
      id={dropdownId}
    >
      <p className="dropdown-animation-label">{label || customLabel}</p>

      <div
        className={`dropdown-animation-wrapper ${isOpen ? `dropdown-animation-wrapper-active` : ""} ${
          disabled ? "dropdown-animation-wrapper-disabled" : ""
        }`}
      >
        <div
          className={`dropdown-animation-button ${isOpen ? `dropdown-animation-button-active ` : ""}`}
          onClick={() => onToggle()}
        >
          {findSelectedOption?.value || findSelectedOption?.key || placeholder}
          {findSelectedOption?.status && (
            <IconTooltip
              iconType={IconTooltipType.INFO}
              className={`icon-tooltip-${findSelectedOption?.status?.key}`}
              tooltipId={`dropdown-animation-tooltip-${findSelectedOption?.key}`}
              tooltipContent={findSelectedOption?.status?.description}
            />
          )}
          {!hideArrow && <ArrowIcon className="icon-dropdown-arrow" />}
        </div>

        <div className={`dropdown-animation-content  ${isOpen ? `dropdown-animation-content-active ` : ""}`}>
          <ul className="list-content">
            {options?.map((option) => {
              const { key, value, icon } = option;
              const isSelected = findSelectedOption?.key === key;
              const optionValue = value || key;

              return (
                <li key={key} className={`item-content ${isSelected ? "item-content-selected" : ""} `} onClick={() => onSelectOption(option)}>
                  {!!icon && icon}
                  {optionValue}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DropdownAnimation;
